import i18n from 'i18next';

import { fetchAiDataPolicyRole } from '@shared/ai-data-policy/access';
import { startAiDataPolicyPolling } from '@shared/ai-data-policy/live';
import { useAiDataPolicyStore } from '@shared/ai-data-policy/store';
import { filesMockMode } from '@shared/lib/dev-mocks';
import { useUIStore } from '@shared/stores/ui.store';

import { watchClosedChatTabs } from './closed-tab-drafts';
import { installConnectAnswers } from './connect-answers';
import { DATA_POLICY_ENABLED } from './data-policy/flag';
import { withDataPolicy } from './data-policy/layer';
import { appendDueSystemNote } from './data-policy/system-notes';
import { setChatService } from './service/ai-cio-service';
import { createFilesMockChatService } from './service/files-mock-service';
import { initAiCioHistorySync } from './service/history-sync';
import { fetchModels } from './service/orchestrator-client';
import { createOrchestratorChatService } from './service/orchestrator-service';
import {
  isBlankConversation,
  setTurnHooks,
  useConversationsStore,
} from './store/conversations.store';
import { isChatOpenInOtherTab } from './tab-binding';

let initialized = false;

/**
 * Activates the real AI CIO backend: swaps the mock chat service for the
 * orchestrator-backed one and starts syncing the CRM conversation-history
 * index. Call once after authentication (from the app layer). Idempotent.
 */
export function initAiCio(): void {
  if (initialized) return;
  initialized = true;
  const mode = filesMockMode();
  if (import.meta.env.DEV && mode) {
    setChatService(createFilesMockChatService({ saveMode: mode }));
  } else {
    const orchestrator = createOrchestratorChatService();
    setChatService(DATA_POLICY_ENABLED ? withDataPolicy(orchestrator) : orchestrator);
    initAiCioHistorySync();
  }
  // Pre-warm the (memoized) model list so the picker is populated instantly.
  void fetchModels();
  // The Connect cards' copy, so a card never suspends the panel while it loads.
  void i18n.loadNamespaces('integrations');
  // The page's one listener for the Connect cards: it answers them by conversation, mounted or
  // not, when a Connect ends (CC D16).
  installConnectAnswers();

  if (DATA_POLICY_ENABLED) {
    void i18n.loadNamespaces('ai-data-policy');
    void useAiDataPolicyStore.getState().load();
    void fetchAiDataPolicyRole();
    startAiDataPolicyPolling(120_000);
    setTurnHooks({
      onTurnFinished: (conversationId) => {
        appendDueSystemNote(conversationId);
      },
    });
    useAiDataPolicyStore.subscribe((state, prev) => {
      if (state.remoteChange === prev.remoteChange || !state.remoteChange) return;
      const activeId = useConversationsStore.getState().activeId;
      if (activeId) appendDueSystemNote(activeId);
    });
  }

  // App-shell surfaces (sidebar logo, Start menu) can't import this module, so
  // they request a brand-new chat by bumping `aiCioNewChatNonce`. Subscribe once
  // here — at module scope, so it's immune to the AiCioTrigger remounting when
  // the tab bar swaps between its 0-tab and tabbed layouts.
  useUIStore.subscribe((state, prev) => {
    if (state.aiCioNewChatNonce === prev.aiCioNewChatNonce) return;
    const store = useConversationsStore.getState();
    const active = store.getActive();
    // A blank chat open in no other tab is reused, and starts as a created one does.
    if (active && isBlankConversation(active) && !isChatOpenInOtherTab(active.id)) {
      store.newChat();
      return;
    }
    store.newChat(undefined, { forceNew: true });
  });

  watchClosedChatTabs();
}
